/** Histórico das publicações feitas pela Berytus no Mercado Livre: o que foi criado, o que falhou e por quê. */
import { Link } from 'react-router';
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Search } from 'lucide-react';
import { Modal } from './CatalogSettings.tsx';
import type { Page, PublicationRow } from '@berytus/contracts';
import { api, brl, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { titleOf } from './Catalog.tsx';
import { Head, Pager, useMeliAccount, useUrlState } from './MeliChannel.tsx';

const STATUS: Record<PublicationRow['status'], { label: string; tone: string }> = {
  queued: { label: 'Na fila', tone: 'info' },
  sending: { label: 'Enviando', tone: 'info' },
  published: { label: 'Publicado', tone: 'ok' },
  failed: { label: 'Recusado', tone: 'err' },
  cancelled: { label: 'Cancelado', tone: 'off' },
};
const EXTERNAL: Record<string, string> = { paused: 'pausado no ML', active: 'ativo no ML', under_review: 'em revisão no ML' };

export function Publications() {
  const { account, isPending, error } = useMeliAccount();
  const [params, set] = useUrlState();
  const page = Number(params.get('page') ?? 1);
  const status = params.get('status') ?? 'all';
  const q = useQuery({
    queryKey: ['meli', 'publications', account?.id, status, page],
    queryFn: () => api<Page<PublicationRow> & { counts: Record<string, number> }>(`/channels/accounts/${account!.id}/publications?status=${status}&page=${page}`),
    enabled: !!account,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => ((query.state.data?.counts.queued ?? 0) + (query.state.data?.counts.sending ?? 0) > 0 ? 3000 : false),
  });
  const counts = q.data?.counts ?? {};
  const [pick, setPick] = useState<{ domain: string; productId: string; title: string } | null>(null);

  return (
    <>
      <Head title="Publicações">
        <p>
          Cada anúncio que a Berytus criou no Mercado Livre. Os anúncios são criados e pausados em seguida:
          confira e ative no Mercado Livre. Recusados mostram o motivo dado pelo canal.
        </p>
      </Head>
      {isPending ? <Loading /> : error ? <ErrorBox error={error} /> : !account ? (
        <div className="panel empty"><b>Nenhuma conta conectada</b>Conecte o Mercado Livre em Integração de canais.</div>
      ) : (
        <>
          <div className="toolbar">
            <div className="seg" role="group" aria-label="Situação">
              {([['all', 'Todas'], ['queued', 'Na fila'], ['published', 'Publicadas'], ['failed', 'Recusadas']] as const).map(([v, l]) => (
                <button key={v} type="button" aria-pressed={status === v} onClick={() => set({ status: v === 'all' ? null : v, page: null })}>
                  {l}{v !== 'all' && counts[v] ? ` (${int(counts[v]!)})` : ''}
                </button>
              ))}
            </div>
            <span className="grow" />
            <Link className="btn sm" to="/canal/mercadolivre/produtos?catalog=ready">Anunciar mais produtos</Link>
          </div>
          {status === 'failed' && <FailureReasons accountId={account.id} />}
          <div className="panel flush">
            {q.error ? <div style={{ padding: 18 }}><ErrorBox error={q.error} /></div>
              : q.isPending ? <Loading />
              : !q.data?.items.length ? <div className="empty"><b>Nenhuma publicação</b>Anuncie a partir de Produtos × anúncios: filtre “Prontos para anunciar”.</div>
              : (
                <div className="grid-wrap">
                  <table className="simple">
                    <thead>
                      <tr><th>Quando</th><th>Produto</th><th>Tipo</th><th className="num">Preço</th><th className="num">Estoque</th><th>Situação</th><th>Anúncio</th><th>Por</th></tr>
                    </thead>
                    <tbody>
                      {q.data.items.map((p) => (
                        <tr key={p.id}>
                          <td className="small">{dateTime(p.createdAt)}</td>
                          <td>
                            <Link to={`/catalogo/${p.productId}`}>{titleOf(p)}</Link>
                            <div className="small muted">SKU {p.sku}</div>
                          </td>
                          <td>{p.listingType === 'gold_pro' ? 'Premium' : 'Clássico'}</td>
                          <td className="num">{brl(p.price)}</td>
                          <td className="num">{int(p.quantity)}</td>
                          <td style={{ maxWidth: 360 }}>
                            <Tag tone={STATUS[p.status].tone}>{STATUS[p.status].label}</Tag>
                            {p.error && <div className="small" style={{ color: p.status === 'failed' ? 'var(--err)' : 'var(--warn)', marginTop: 4 }}>{p.error}</div>}
                            {p.status === 'failed' && /domínio (MLB-[A-Z0-9_]+)/.test(p.error ?? '') && /categoria/.test(p.error ?? '') && (
                              <button className="btn sm" type="button" style={{ marginTop: 6 }}
                                onClick={() => setPick({ domain: /domínio (MLB-[A-Z0-9_]+)/.exec(p.error!)![1]!, productId: p.productId, title: titleOf(p) })}>
                                Escolher categoria
                              </button>
                            )}
                          </td>
                          <td>
                            {p.externalId ? (
                              <>
                                {p.permalink ? <a className="ext" href={p.permalink} target="_blank" rel="noreferrer">{p.externalId} <ExternalLink size={11} /></a> : p.externalId}
                                {p.externalStatus && <div className="small muted">{EXTERNAL[p.externalStatus] ?? p.externalStatus}</div>}
                              </>
                            ) : '—'}
                          </td>
                          <td className="small muted">{p.createdBy ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            {q.data && <Pager total={q.data.total} page={page} pageSize={q.data.pageSize} onPage={(n) => set({ page: n > 1 ? String(n) : null })} />}
          </div>
          {pick && account && <DomainCategory accountId={account.id} {...pick} onClose={() => setPick(null)} />}
        </>
      )}
    </>
  );
}

/**
 * Ficha do catálogo sem categoria conhecida: escolhe a categoria do domínio (vale para todas as fichas dele)
 * e publica o produto de novo.
 */
function DomainCategory({ accountId, domain, productId, title, onClose }: { accountId: string; domain: string; productId: string; title: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState(title);
  const [term, setTerm] = useState(title);
  const r = useQuery({
    queryKey: ['meli', 'predict', term],
    queryFn: () => api<{ id: string; name: string | null; path: string | null; domain: string | null }[]>(`/meli/categories/predict?q=${encodeURIComponent(term)}`),
    enabled: term.trim().length >= 2,
  });
  const save = useMutation({
    mutationFn: async (categoryId: string) => {
      await api(`/meli/domains/${domain}/category`, { method: 'POST', json: { categoryId } });
      return api<{ queued: number; skipped: number }>(`/channels/accounts/${accountId}/publish`, { method: 'POST', json: { productIds: [productId] } });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['meli'] }); onClose(); },
  });
  return (
    <Modal title="Categoria do domínio no Mercado Livre" onClose={onClose}>
      <p className="small muted" style={{ margin: 0 }}>
        O Mercado Livre não informa a categoria das fichas do domínio <b className="code">{domain}</b> (nenhum vendedor anuncia nelas ainda).
        Escolha a categoria uma vez: ela vale para todos os produtos desse domínio, e este produto é publicado de novo.
      </p>
      <form style={{ display: 'flex', gap: 8 }} onSubmit={(e) => { e.preventDefault(); setTerm(text); }}>
        <input className="input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Descreva o produto (ex.: analgésico urinário)" style={{ flex: 1 }} />
        <button className="btn" type="submit"><Search size={14} /> Buscar</button>
      </form>
      {r.isFetching && <Loading />}
      {r.error && <ErrorBox error={r.error} />}
      {r.data?.length === 0 && <span className="small muted">Nenhuma categoria sugerida. Tente outras palavras (ex.: "medicamento", "remédio para infecção urinária").</span>}
      <div style={{ display: 'grid', gap: 6 }}>
        {r.data?.map((c) => (
          <button key={c.id} type="button" className="btn" disabled={save.isPending} style={{ justifyContent: 'flex-start', textAlign: 'left', height: 'auto', padding: '8px 12px' }} onClick={() => save.mutate(c.id)}>
            <span><b>{c.name}</b><span className="small muted"> · {c.path} · {c.id}{c.domain ? ` · ${c.domain}` : ''}</span></span>
          </button>
        ))}
      </div>
      {save.error && <ErrorBox error={save.error} />}
    </Modal>
  );
}

interface Failures {
  total: number;
  groups: { reason: string; count: number; products: number; image: boolean; example: string }[];
  retry: { status: string; error: string | null; createdAt: string; finishedAt: string | null;
    stats: { candidates?: number; treated?: number; valid?: number; noValidImage?: number; queued?: number; skipped?: number; pendingApproval?: boolean } | null } | null;
}

/**
 * Recusas agrupadas por motivo (código + mensagem resumida) e o reprocessamento das recusadas por imagem: as fotos
 * são tratadas para o ML e as que ficam válidas voltam para a fila (pelo disjuntor de ações em massa).
 */
function FailureReasons({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['meli', 'publications', 'failures', accountId],
    queryFn: () => api<Failures>(`/channels/accounts/${accountId}/publications/failures`),
    refetchInterval: (query) => (['queued', 'running'].includes(query.state.data?.retry?.status ?? '') ? 4000 : false),
  });
  const retry = useMutation({
    mutationFn: () => api(`/channels/accounts/${accountId}/publications/retry-images`, { method: 'POST', json: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli', 'publications'] }),
  });
  const d = q.data;
  if (q.error) return <ErrorBox error={q.error} />;
  if (!d || !d.total) return null;
  const image = d.groups.filter((g) => g.image).reduce((a, g) => a + g.count, 0);
  const r = d.retry;
  const running = !!r && ['queued', 'running'].includes(r.status);
  const s = r?.stats ?? {};
  return (
    <section className="panel stack-y" style={{ gap: 8 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Recusas por motivo</h2>
        <span className="small muted">{int(d.total)} recusa(s) · {int(image)} por imagem</span>
        <span className="grow" />
        <button type="button" className="btn sm primary" disabled={!image || running || retry.isPending}
          title="Trata as fotos (corte das bordas brancas, mínimo de 500 px, sem ampliar) e reenfileira as que ficarem válidas. O anúncio nasce pausado."
          onClick={() => { if (window.confirm(`Reprocessar as recusadas por imagem? As fotos são tratadas e os produtos com foto válida voltam para a fila (anúncio nasce pausado).`)) retry.mutate(); }}>
          {running ? 'Reprocessando…' : 'Reprocessar recusadas por imagem'}
        </button>
      </div>
      {retry.error && <ErrorBox error={retry.error} />}
      {r && (
        <div className={`notice small${r.status === 'failed' ? ' warn' : ''}`}>
          {running ? <>Reprocessando: {int(s.treated ?? 0)} de {int(s.candidates ?? 0)} produto(s) conferido(s)…</>
            : r.status === 'failed' ? <>O último reprocessamento falhou: {r.error}</>
            : <>Último reprocessamento ({dateTime(r.finishedAt ?? r.createdAt)}): {int(s.candidates ?? 0)} produto(s) recusado(s) por imagem ·{' '}
                <b>{int(s.valid ?? 0)} com foto válida</b>{s.pendingApproval ? ' (aguardando aprovação no Painel Berytus › Compliance)' : ` · ${int(s.queued ?? 0)} na fila de novo`} ·{' '}
                {int(s.noValidImage ?? 0)} sem foto válida (envie uma foto maior){s.skipped ? ` · ${int(s.skipped)} já tinham anúncio` : ''}</>}
        </div>
      )}
      <div className="grid-wrap">
        <table className="simple">
          <thead><tr><th>Motivo</th><th className="num">Recusas</th><th className="num">Produtos</th></tr></thead>
          <tbody>
            {d.groups.slice(0, 30).map((g) => (
              <tr key={g.reason}>
                <td style={{ maxWidth: 640, whiteSpace: 'normal' }} title={g.example}>{g.image && <Tag tone="warn">imagem</Tag>} {g.reason}</td>
                <td className="num">{int(g.count)}</td>
                <td className="num">{int(g.products)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
